/* ============================================================
   Integration registry — the FOUNDER RULE, encoded.
   Every contract source is either wired to a live system NOW, or stubbed with
   placeholder data behind a "coming soon" / "connect me" badge + a flag. The
   server decides live-vs-placeholder at request time (it tries the real
   integration and degrades gracefully), and stamps each response with a
   `source`. This registry is the client-side map of intent + copy.
   ============================================================ */
import type { ContractKey } from './contract';

export type SourceState = 'live' | 'placeholder';

export interface Integration {
  /** Contract key this source feeds. */
  key: ContractKey | 'login';
  /** Human label for the connector. */
  label: string;
  /** The system we wire to (or will). */
  system: string;
  /** Whether it's meant to be connectable now, or is gated on an [I DO]. */
  intent: 'wire-now' | 'coming-soon';
  /** Copy for the "connect me" badge when running on placeholder data. */
  connect: string;
}

export const INTEGRATIONS: Integration[] = [
  { key: 'login', label: 'Sign-in', system: 'Google SSO / Cloudflare Access', intent: 'coming-soon', connect: 'Connect Cloudflare Access' },
  { key: 'health', label: 'System health', system: 'Cloudflare · GitHub · Sentry · Anthropic', intent: 'wire-now', connect: 'Connect status sources' },
  { key: 'activity', label: 'Activity feed', system: 'Slack + event bus', intent: 'wire-now', connect: 'Connect Slack' },
  { key: 'crew', label: 'Crew', system: 'Orchestrator runtime', intent: 'wire-now', connect: 'Connect the orchestrator' },
  { key: 'site', label: 'Site analytics', system: 'Cloudflare Analytics + Resend', intent: 'wire-now', connect: 'Connect Cloudflare Analytics' },
  { key: 'code', label: 'Code', system: 'GitHub API', intent: 'wire-now', connect: 'Connect GitHub' },
  { key: 'budget', label: 'AI budget', system: 'Anthropic usage', intent: 'coming-soon', connect: 'Connect Anthropic usage' },
  { key: 'money', label: 'Spend', system: 'Anthropic + infra billing', intent: 'coming-soon', connect: 'Connect billing' },
  { key: 'reviews', label: 'Reviews', system: 'App Store Connect', intent: 'coming-soon', connect: 'Connect App Store Connect' },
  { key: 'perf', label: 'Performance', system: 'App Store Connect + Sentry', intent: 'coming-soon', connect: 'Connect App Store Connect' },
  { key: 'approvals', label: 'Approvals queue', system: 'D1 (portal)', intent: 'wire-now', connect: 'Connect the D1 queue' },
  { key: 'tasks', label: 'Crew tasks', system: 'D1 + Slack dispatch', intent: 'wire-now', connect: 'Connect the task bridge' },
  { key: 'inbox', label: 'Founder inbox', system: 'Notion (Founder Inbox)', intent: 'wire-now', connect: 'Connect the Founder Inbox database' },
  { key: 'plan', label: 'Project queue', system: 'Notion (Roadmap)', intent: 'wire-now', connect: 'Connect the Roadmap database' },
  { key: 'ido', label: 'My [I DO] list', system: 'D1 (portal)', intent: 'wire-now', connect: 'Connect the D1 list' },
  { key: 'chat', label: 'Talk to AI', system: 'Anthropic via Cloudflare AI Gateway', intent: 'coming-soon', connect: 'Paste ANTHROPIC_API_KEY' },
  { key: 'threads', label: 'Agent threads', system: 'Orchestrator runtime', intent: 'wire-now', connect: 'Connect the orchestrator' },
  { key: 'heartbeat', label: 'Machine heartbeat', system: 'Always-on box → /api/heartbeat', intent: 'wire-now', connect: 'Start the heartbeat cron' },
];

const BY_KEY = new Map(INTEGRATIONS.map((i) => [i.key, i]));
export function integrationFor(key: ContractKey | 'login'): Integration | undefined {
  return BY_KEY.get(key);
}
